import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CreditCard, User, Phone, FileText } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import KioskLayout from "@/components/KioskLayout";
import Numpad from "@/components/Numpad";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function KioskIdentify() {
  const navigate = useNavigate();
  const { action } = useParams();

  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [preRegisterMode, setPreRegisterMode] = useState(false);
  const [preRegisterName, setPreRegisterName] = useState("");
  const [preRegisterPhone, setPreRegisterPhone] = useState("");

  const [insurances, setInsurances] = useState([]);
  const [loadingInsurances, setLoadingInsurances] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState({
    id: null,
    convenio_id: null,
    name: "PARTICULAR",
    label: "Sem convênio",
    is_private: true,
  });

  const formatCPF = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const normalizeText = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toUpperCase();

  const displayValue = formatCPF(identifier);
  const isValid = identifier.length === 11;

  const getInsuranceId = (insurance) =>
    insurance?.id ||
    insurance?.convenio_id ||
    insurance?.insurance_id ||
    insurance?.raw?.id ||
    insurance?.raw?.convenio_id ||
    null;

  const getInsuranceName = (insurance) =>
    insurance?.name ||
    insurance?.nome ||
    insurance?.descricao ||
    insurance?.convenio ||
    insurance?.raw?.name ||
    insurance?.raw?.nome ||
    insurance?.raw?.descricao ||
    "";

  const isBlockedInsurance = (name) => {
    const normalized = normalizeText(name);
    return (
      normalized.includes("RETORNO") ||
      normalized.includes("REPRESENTANTE")
    );
  };

  const isPrivateInsurance = (name) => {
    const normalized = normalizeText(name);
    return (
      normalized === "PARTICULAR" ||
      normalized === "SEM CONVENIO" ||
      normalized === "SEM CONVÊNIO" ||
      normalized === "OUTRO" ||
      normalized.includes("PARTICULAR")
    );
  };

  const getCanonicalInsuranceName = (name) => {
    const normalized = normalizeText(name);

    if (normalized.includes("UNIMED")) return "UNIMED";
    if (normalized.includes("SULAME")) return "SULAMÉRICA";
    if (normalized.includes("TETRATA")) return "TETRATA";
    if (normalized.includes("UNIPAX")) return "UNIPAX";
    if (normalized.includes("VIVA BEM")) return "VIVA BEM";
    if (normalized.includes("SAS")) return "SAS";
    if (normalized.includes("SINTRAS")) return "SINTRAS";
    if (normalized.includes("HOSPITALAR")) return "HOSPITALAR";

    return String(name || "").trim();
  };

  const normalizeInsuranceList = (items = []) => {
    const result = [];
    const seen = new Set();

    for (const item of items) {
      const id = getInsuranceId(item);
      const originalName = getInsuranceName(item);
      const canonicalName = getCanonicalInsuranceName(originalName);
      const normalizedName = normalizeText(canonicalName);

      if (!originalName || !id) {
        continue;
      }

      if (isBlockedInsurance(originalName) || isPrivateInsurance(originalName)) {
        continue;
      }

      if (seen.has(normalizedName)) {
        continue;
      }

      seen.add(normalizedName);

      result.push({
        ...item,
        id,
        convenio_id: id,
        name: canonicalName,
        label: "Convênio",
        is_private: false,
      });
    }

    return result.sort((a, b) =>
      getInsuranceName(a).localeCompare(getInsuranceName(b), "pt-BR")
    );
  };

  const fetchInsurances = async () => {
    setLoadingInsurances(true);

    try {
      const response = await axios.get(`${API_URL}/api/insurances`);

      if (response.data.success) {
        setInsurances(normalizeInsuranceList(response.data.insurances || []));
      } else {
        setInsurances([]);
        toast.error("Não foi possível carregar os convênios. Você pode seguir como particular.");
      }
    } catch (error) {
      console.error("Error fetching insurances:", error);
      setInsurances([]);
      toast.error("Não foi possível carregar os convênios. Você pode seguir como particular.");
    } finally {
      setLoadingInsurances(false);
    }
  };

  useEffect(() => {
    if (preRegisterMode) {
      fetchInsurances();
    }
  }, [preRegisterMode]);

  const handleSearch = async () => {
    if (!isValid) {
      toast.error("CPF inválido. Digite os 11 dígitos.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/patients/search`, {
        identifier: identifier,
        identifier_type: "cpf",
      });

      if (response.data.success && response.data.patient) {
        sessionStorage.setItem("patient", JSON.stringify(response.data.patient));
        navigate(`/appointments/${action}`);
        return;
      }

      if (action === "checkin") {
        toast.error("Paciente não encontrado. Procure a recepção.");
        return;
      }

      setSelectedInsurance({
        id: null,
        convenio_id: null,
        name: "PARTICULAR",
        label: "Sem convênio",
        is_private: true,
      });
      setPreRegisterMode(true);
      toast.info("Paciente não encontrado. Faça um pré-cadastro para continuar.");
    } catch (error) {
      console.error("Search error:", error);

      if (error.response?.data?.detail?.includes("API Key")) {
        toast.error("Sistema não configurado. Por favor, contate a recepção.");
        return;
      }

      if (action === "checkin") {
        toast.error("Paciente não encontrado. Procure a recepção.");
        return;
      }

      setSelectedInsurance({
        id: null,
        convenio_id: null,
        name: "PARTICULAR",
        label: "Sem convênio",
        is_private: true,
      });
      setPreRegisterMode(true);
      toast.info("Paciente não encontrado. Faça um pré-cadastro para continuar.");
    } finally {
      setLoading(false);
    }
  };

  const handlePreRegister = async () => {
    const name = preRegisterName.trim();
    const phone = preRegisterPhone.replace(/\D/g, "");

    if (!name || name.length < 5) {
      toast.error("Informe o nome completo do paciente.");
      return;
    }

    if (!phone || phone.length < 10) {
      toast.error("Informe um celular válido.");
      return;
    }

    const selectedInsuranceId =
      selectedInsurance && !selectedInsurance.is_private
        ? getInsuranceId(selectedInsurance)
        : null;

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/patients/pre-register`, {
        cpf: identifier,
        name,
        phone,
        convenio_id: selectedInsuranceId ? Number(selectedInsuranceId) : null,
        convenio_plano_id: selectedInsuranceId ? 0 : null,
      });

      if (response.data.success && response.data.patient) {
        const patientData = {
          ...response.data.patient,
          convenio_id: selectedInsuranceId ? Number(selectedInsuranceId) : null,
          convenios: selectedInsuranceId
            ? [
                {
                  convenio_id: Number(selectedInsuranceId),
                  plano_id: 0,
                },
              ]
            : [],
          selected_insurance_name: selectedInsurance?.name || "PARTICULAR",
        };

        sessionStorage.setItem("patient", JSON.stringify(patientData));
        toast.success("Pré-cadastro realizado com sucesso.");
        navigate(`/appointments/${action}`);
        return;
      }

      toast.error("Não foi possível realizar o pré-cadastro. Procure a recepção.");
    } catch (error) {
      console.error("Pre-register error:", error);
      toast.error(
        error.response?.data?.detail ||
          "Não foi possível realizar o pré-cadastro. Procure a recepção."
      );
    } finally {
      setLoading(false);
    }
  };

  const resetPreRegister = () => {
    setPreRegisterMode(false);
    setPreRegisterName("");
    setPreRegisterPhone("");
    setSelectedInsurance({
      id: null,
      convenio_id: null,
      name: "PARTICULAR",
      label: "Sem convênio",
      is_private: true,
    });
  };

  return (
    <KioskLayout showBack step="Identificação">
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4 font-['Outfit'] tracking-tight">
            {preRegisterMode ? "Pré-cadastro" : "Identificação"}
          </h2>

          <p className="text-xl md:text-2xl text-slate-500 mb-12">
            {preRegisterMode
              ? "Complete os dados para continuar o agendamento"
              : action === "checkin"
              ? "Informe seu CPF para confirmar presença"
              : "Informe seu CPF para remarcar ou marcar sua consulta"}
          </p>

          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[#00357C]/10 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-[#00357C]" />
            </div>
            <span className="text-2xl font-semibold text-slate-700">CPF</span>
          </div>

          <div className="mb-8">
            <input
              type="text"
              value={displayValue}
              readOnly
              placeholder="000.000.000-00"
              className="w-full max-w-xl h-20 md:h-24 text-3xl md:text-4xl text-center rounded-2xl border-2 border-slate-300 focus:border-[#00357C] focus:ring-4 focus:ring-[#00357C]/20 outline-none bg-white"
              data-testid="identifier-input"
            />
          </div>

          {!preRegisterMode ? (
            <>
              <div className="mb-12 max-w-xl mx-auto">
                <Numpad value={identifier} onChange={setIdentifier} maxLength={11} />
              </div>

              <Button
                data-testid="search-patient-btn"
                onClick={handleSearch}
                disabled={!isValid || loading}
                className="kiosk-primary-btn w-full max-w-xl"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                    Buscando...
                  </span>
                ) : (
                  "Continuar"
                )}
              </Button>
            </>
          ) : (
            <>
              <div className="space-y-5 mb-8 text-left max-w-2xl mx-auto">
                <label className="block">
                  <span className="flex items-center gap-2 text-xl font-semibold text-slate-700 mb-2">
                    <User className="w-5 h-5 text-[#00357C]" />
                    Nome completo
                  </span>
                  <input
                    type="text"
                    value={preRegisterName}
                    onChange={(e) => setPreRegisterName(e.target.value)}
                    placeholder="Digite o nome completo"
                    className="w-full h-16 text-2xl rounded-2xl border-2 border-slate-300 focus:border-[#00357C] focus:ring-4 focus:ring-[#00357C]/20 outline-none bg-white px-5"
                  />
                </label>

                <label className="block">
                  <span className="flex items-center gap-2 text-xl font-semibold text-slate-700 mb-2">
                    <Phone className="w-5 h-5 text-[#00357C]" />
                    Celular
                  </span>
                  <input
                    type="tel"
                    value={formatPhone(preRegisterPhone)}
                    onChange={(e) =>
                      setPreRegisterPhone(e.target.value.replace(/\D/g, "").slice(0, 11))
                    }
                    placeholder="(00) 00000-0000"
                    className="w-full h-16 text-2xl rounded-2xl border-2 border-slate-300 focus:border-[#00357C] focus:ring-4 focus:ring-[#00357C]/20 outline-none bg-white px-5"
                  />
                </label>

                <div className="block">
                  <span className="flex items-center gap-2 text-xl font-semibold text-slate-700 mb-3">
                    <FileText className="w-5 h-5 text-[#00357C]" />
                    Convênio
                  </span>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedInsurance({
                          id: null,
                          convenio_id: null,
                          name: "PARTICULAR",
                          label: "Sem convênio",
                          is_private: true,
                        })
                      }
                      className={`rounded-2xl border-2 p-4 text-left transition-all ${
                        selectedInsurance?.is_private
                          ? "border-[#00357C] bg-[#00357C]/10"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <p className="text-xl font-bold text-slate-800">PARTICULAR</p>
                      <p className="text-base text-slate-500">Sem convênio</p>
                    </button>

                    {loadingInsurances ? (
                      <div className="rounded-2xl border-2 border-slate-200 p-4 text-center text-slate-500">
                        Carregando convênios...
                      </div>
                    ) : (
                      insurances.map((insurance) => {
                        const insuranceId = getInsuranceId(insurance);
                        const insuranceName = getInsuranceName(insurance);
                        const selected =
                          !selectedInsurance?.is_private &&
                          String(getInsuranceId(selectedInsurance) || "") === String(insuranceId || "");

                        return (
                          <button
                            key={`${insuranceName}-${insuranceId}`}
                            type="button"
                            onClick={() => setSelectedInsurance(insurance)}
                            className={`rounded-2xl border-2 p-4 text-left transition-all ${
                              selected
                                ? "border-[#00357C] bg-[#00357C]/10"
                                : "border-slate-200 bg-white hover:border-slate-300"
                            }`}
                          >
                            <p className="text-xl font-bold text-slate-800">{insuranceName}</p>
                            <p className="text-lg text-slate-500">Convênio</p>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4 max-w-2xl mx-auto">
                <Button
                  data-testid="pre-register-btn"
                  onClick={handlePreRegister}
                  disabled={loading}
                  className="kiosk-primary-btn w-full"
                >
                  {loading ? "Salvando..." : "Criar pré-cadastro e continuar"}
                </Button>

                <Button
                  onClick={resetPreRegister}
                  disabled={loading}
                  className="kiosk-secondary-btn w-full"
                >
                  Voltar para CPF
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </KioskLayout>
  );
}
